import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import EmojiPeopleIcon from "@material-ui/icons/EmojiPeople";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import HomeIcon from "@material-ui/icons/Home";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import M_ManageEntityForm from "./AddEntity/M_ManageEntityForm";

const HOME_URL = "/home/e79fd120-30ed-46eb-b4b1-54b43b9d1d14/19ce9808-de97-4c75-9089-0df544835f7c";

const useStyles = makeStyles(() => ({
  // ── shared ───────────────────────────────────────────────────────────────
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "12px 8px 4px",
    gap: "14px",
  },
  iconCircle: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    background: "linear-gradient(135deg, var(--color-primary-main) 0%, var(--sidebar-background, var(--color-primary-main)) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
  },
  icon: {
    fontSize: "2.2rem",
    color: "#fff",
  },
  successIconCircle: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    backgroundColor: "#e8f5e9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  successIcon: {
    fontSize: "2.4rem",
    color: "#2e7d32",
  },
  warnIconCircle: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    backgroundColor: "#fff8e1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  warnIcon: {
    fontSize: "2.4rem",
    color: "#f9a825",
  },
  heading: {
    fontWeight: 800,
    fontSize: "1.45rem",
    color: "var(--color-text-primary)",
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
  },
  subheading: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "var(--color-primary-main)",
    lineHeight: 1.3,
  },
  description: {
    fontSize: "0.875rem",
    color: "var(--color-text-secondary)",
    lineHeight: 1.65,
    maxWidth: 310,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "10px",
    width: "100%",
    paddingTop: "6px",
    paddingBottom: "4px",
  },
}));

const M_ContactRequiredDialog = ({ contactEntity, onContactCreated, onSkip }) => {
  const classes = useStyles();
  const history = useHistory();
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedContactId, setSavedContactId] = useState(null);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  // Closing (welcome ✕ or form ✕) shows the soft skip warning instead of dismissing outright.
  const requestSkip = () => {
    setShowSkipConfirm(true);
  };

  // Actually persist the skip (parent writes localStorage + dismisses).
  const confirmSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  const handleSaveSuccess = (responseObj) => {
    // Unmount the form immediately (before the generic alert renders) and show custom success screen.
    // Do NOT call onContactCreated here — that would set hasContact=true in the parent,
    // unmounting this component before showSuccess=true can render.
    setShowForm(false);
    setSavedContactId(responseObj && responseObj.id ? responseObj.id : null);
    setShowSuccess(true);
  };

  const navigateTo = (url) => {
    // Now safe to notify parent (triggers hasContact=true + background localStorage refresh)
    // and navigate — component unmount from parent happens on next render, after push.
    onContactCreated();
    history.push(url);
  };

  // ── Skip confirmation: soft warning, stacked over the form on close ───────
  const skipConfirmDialog = showSkipConfirm ? (
    <A_SimpleDialog
      open={true}
      title=""
      maxWidth="xs"
      fullWidth={true}
      showclosebutton="false"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      dialogContent={
        <div className={classes.root}>
          <div className={classes.warnIconCircle}>
            <InfoOutlinedIcon className={classes.warnIcon} />
          </div>

          <Typography className={classes.heading}>Skip for now?</Typography>

          <Typography className={classes.description}>
            If you don&apos;t create your contact, some functionality might not
            work as expected. You can complete this setup anytime later.
          </Typography>

          <div className={classes.actions}>
            <A_Button
              color="primary"
              variant="contained"
              size="medium"
              label="Continue setup"
              onClick={() => setShowSkipConfirm(false)}
            />
            <A_Button
              color="primary"
              variant="text"
              size="medium"
              label="Skip anyway"
              onClick={confirmSkip}
            />
          </div>
        </div>
      }
    />
  ) : null;

  // ── Step 3: custom success screen ───────────────────────────────────────
  if (showSuccess) {
    const openRecordUrl =
      savedContactId && contactEntity && contactEntity.pageUrl
        ? `/${contactEntity.pageUrl}/${savedContactId}`
        : null;

    const successContent = (
      <div className={classes.root}>
        <div className={classes.successIconCircle}>
          <CheckCircleOutlineIcon className={classes.successIcon} />
        </div>

        <Typography className={classes.heading}>
          You&apos;re all set!
        </Typography>

        <Typography className={classes.description}>
          Your contact record has been created. You can view your profile or
          head straight to the home page.
        </Typography>

        <div className={classes.actions}>
          {openRecordUrl && (
            <A_Button
              color="primary"
              variant="contained"
              size="medium"
              label="Open my record"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => navigateTo(openRecordUrl)}
            />
          )}
          <A_Button
            color="primary"
            variant="outlined"
            size="medium"
            label="Go to Home"
            endIcon={<HomeIcon fontSize="small" />}
            onClick={() => navigateTo(HOME_URL)}
          />
        </div>
      </div>
    );

    return (
      <A_SimpleDialog
        open={true}
        title=""
        maxWidth="xs"
        fullWidth={true}
        showclosebutton="false"
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        dialogContent={successContent}
      />
    );
  }

  // ── Step 2: contact form (skip popup stacks on top when closing) ──────────
  if (showForm && contactEntity) {
    return (
      <>
        <M_ManageEntityForm
          fromAddEntity={true}
          entity={contactEntity}
          open={true}
          onClose={requestSkip}
          disableClose={false}
          closeWithoutWarning={true}
          showPopup={false}
          onSaveSuccess={handleSaveSuccess}
          refresh={true}
        />
        {skipConfirmDialog}
      </>
    );
  }

  // ── Step 1: welcome screen ───────────────────────────────────────────────
  const infoContent = (
    <div className={classes.root}>
      <div className={classes.iconCircle}>
        <EmojiPeopleIcon className={classes.icon} />
      </div>

      <Typography className={classes.heading}>
        Welcome!
      </Typography>

      <Typography className={classes.subheading}>
        One quick step before you dive in
      </Typography>

      <Typography className={classes.description}>
        We need to set up your contact profile before you can access the portal.
        It only takes a minute and is a one-time setup.
      </Typography>

      <div className={classes.actions}>
        <A_Button
          color="primary"
          variant="contained"
          size="medium"
          label="Get Started"
          endIcon={<ArrowForwardIcon fontSize="small" />}
          onClick={() => setShowForm(true)}
        />
      </div>
    </div>
  );

  return (
    <>
      <A_SimpleDialog
        open={true}
        title=""
        maxWidth="xs"
        fullWidth={true}
        showclosebutton="true"
        onClose={requestSkip}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        dialogContent={infoContent}
      />
      {skipConfirmDialog}
    </>
  );
};

M_ContactRequiredDialog.propTypes = {
  contactEntity: PropTypes.object,
  onContactCreated: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default M_ContactRequiredDialog;
